import Decimal from "decimal.js-light";

import {
  INCHES_PER_FOOT,
  LINEAL_FEET_STORAGE_DECIMALS,
  METERS_PER_FOOT,
  METERS_PER_INCH,
  MM_PER_FOOT,
  MM_PER_INCH,
  SQ_FT_DISPLAY_DECIMALS,
  SQ_FT_STORAGE_DECIMALS,
  SQ_METER_PER_SQ_FOOT,
} from "./constant";

/** SQUARE METERS UTILITIES */
/**
 * Converts square meters to square feet for display.
 * This is the inverse of convertSquareFeetToSquareMeters.
 *
 * @param {number} [squareMeters=0] - The area in square meters
 * @returns {number} The area in square feet with 4 decimal places (display precision)
 */
export const convertSquareMetersToSquareFeet = (squareMeters = 0) => {
  const numSquareMeters = Number(squareMeters) || 0;
  return parseFloat(
    Decimal(numSquareMeters).dividedBy(SQ_METER_PER_SQ_FOOT).toDecimalPlaces(SQ_FT_DISPLAY_DECIMALS)
  );
};

/**
 * Converts square feet to square meters for storage.
 * Uses 6 decimal places to ensure lossless round-trip when displaying 4-decimal sq ft.
 * This is the inverse of convertSquareMetersToSquareFeet.
 *
 * @param {number|string} [squareFeet=0] - The area in square feet
 * @returns {number} The area in square meters (6 decimal places for storage)
 */
export const convertSquareFeetToSquareMeters = (squareFeet = 0) => {
  const numSquareFeet = Number(squareFeet) || 0;
  return parseFloat(
    Decimal(numSquareFeet).times(SQ_METER_PER_SQ_FOOT).toDecimalPlaces(SQ_FT_STORAGE_DECIMALS)
  );
};

/** LINEAL FEET UTILITIES */
/**
 * Converts millimeters to feet decimal number.
 *
 * @param {number} [millimeters=0] - The length in millimeters to convert
 * @returns {number} The length in feet as a decimal number (4 decimal places)
 */
export const convertMmToFeet = (mm = 0) => {
  return parseFloat(
    Decimal(mm || 0)
      .dividedBy(MM_PER_FOOT)
      .toDecimalPlaces(4)
  );
};

/**
 * Converts meters to feet decimal number.
 *
 * @param {number} [meters=0] - The length in meters to convert
 * @returns {number} The length in feet as a decimal number (4 decimal places)
 */
export const convertMetersToFeet = (meters = 0) => {
  return parseFloat(
    Decimal(meters || 0)
      .dividedBy(METERS_PER_FOOT)
      .toDecimalPlaces(4)
  );
};

/**
 * Converts feet and inches to meters.
 * This is used when saving US measurements to the database.
 * Handles fractional inches (e.g., 5.5 inches or "5 1/2" after normalization).
 * Uses 6 decimal places for meters to avoid precision loss when round-tripping
 * with convertMetersToFeetInches (same rationale as SQ_FT_STORAGE_DECIMALS for sqm).
 * This is the inverse of convertMetersToFeetInches.
 *
 * @param {Object} [params={}] - The measurement object
 * @param {number} [params.feet=0] - The number of feet
 * @param {number} [params.inches=0] - The number of inches (can be fractional)
 * @returns {number} The length in meters (with precision up to 6 decimal places for storage)
 */
export const convertFeetInchesToMeters = ({ feet = 0, inches = 0 } = {}) => {
  const numFeet = Decimal(feet || 0);
  const numInches = Decimal(inches || 0);

  const metersFromFeet = numFeet.times(METERS_PER_FOOT);
  const metersFromInches = numInches.times(METERS_PER_INCH);

  return parseFloat(
    metersFromFeet.plus(metersFromInches).toDecimalPlaces(LINEAL_FEET_STORAGE_DECIMALS)
  );
};

/**
 * Converts meters to feet and inches.
 * This is used when displaying database values in US measurement format.
 * This is the inverse of convertFeetInchesToMeters.
 *
 * @param {number} [meters=0] - The length in meters to convert
 * @returns {{feet: number, inches: number}} An object with feet and inches properties
 */
export const convertMetersToFeetInches = (meters = 0) => {
  const numMeters = Decimal(meters);

  // Convert meters to total inches
  const totalInches = numMeters.dividedBy(METERS_PER_INCH);

  // Extract whole feet (floor)
  const feet = totalInches.dividedBy(INCHES_PER_FOOT).toDecimalPlaces(0, Decimal.ROUND_FLOOR);

  // Get remaining inches
  const inches = totalInches.minus(feet.times(INCHES_PER_FOOT));

  return {
    feet: parseFloat(feet),
    inches: parseFloat(inches),
  };
};

/**
 * Converts feet and inches to millimeters.
 * This is the inverse of convertMmToFeetInches (round-trip may have small loss; mm is rounded to 4 decimals).
 *
 * @param {Object} [params={}] - The measurement object
 * @param {number} [params.feet=0] - The number of feet
 * @param {number} [params.inches=0] - The number of inches
 * @returns {number} The length in millimeters (4 decimal places)
 */
export const convertFeetInchesToMm = ({ feet = 0, inches = 0 } = {}) => {
  const totalInches = Decimal(feet || 0)
    .times(INCHES_PER_FOOT)
    .plus(Decimal(inches || 0));
  const resultInMm = totalInches.times(MM_PER_INCH);

  /*
   * Sweet spot is 4 decimal places to avoid floating-point noise
   */
  return parseFloat(resultInMm.toDecimalPlaces(4));
};

/**
 * Converts millimeters to feet and inches.
 * This is the inverse of convertFeetInchesToMm.
 * Uses Decimal for division so round-trip with convertFeetInchesToMm is deterministic (limited by 4-decimal mm).
 *
 * @param {number} [millimeters=0] - The length in millimeters to convert
 * @returns {{feet: number, inches: number}} An object with feet and inches properties
 */
export const convertMmToFeetInches = (mm = 0) => {
  const totalInches = Decimal(mm || 0).dividedBy(MM_PER_INCH);

  /*
   * Why we use Math.floor for feet
   * Feet are whole units, we don't want "partial feet" in the display.
   * Using Math.floor guarantees that the number of feet is never overestimated.
   */
  const feet = Math.floor(totalInches.dividedBy(INCHES_PER_FOOT).toNumber());
  const inches = totalInches.minus(Decimal(feet).times(INCHES_PER_FOOT));

  return {
    feet: parseFloat(feet),
    inches: parseFloat(inches.toDecimalPlaces(4)),
  };
};

/**
 * Converts a decimal feet value to feet and inches.
 *
 * @param {number} [feetDecimal=0] - The length in decimal feet to convert
 * @returns {{feet: number, inches: number}} An object with feet and inches properties
 */
export const convertFeetToFeetInches = (feetDecimal = 0) => {
  const totalInches = Decimal(feetDecimal || 0).times(INCHES_PER_FOOT);
  const feet = Math.floor(totalInches.dividedBy(INCHES_PER_FOOT).toNumber());
  const inches = totalInches.minus(Decimal(feet).times(INCHES_PER_FOOT));

  return {
    feet: parseFloat(feet),
    inches: parseFloat(inches.toDecimalPlaces(4)),
  };
};
